/**
 * Pure helpers over the WOO's availableMaterials custom field, written by the
 * dynamics365-connector's available-materials sync. The field is a map keyed
 * by item number:
 *
 *   {
 *     "<itemNumber>": {
 *       requiredQty: 24,
 *       qtyPerUnit: 0.024,   // quantity consumed per produced part
 *       unit: "pcs",
 *       lps: [ { lp, onHand, warehouse, location?, batch? }, ... ]
 *     }
 *   }
 *
 * The connector does not write a description per material; itemNumber is the
 * display name throughout the picking UI.
 */

/**
 * Flattens the availableMaterials map into an array of requirement rows:
 * { itemNumber, requiredQty, qtyPerUnit, unit, lps }.
 * Empty array when the WOO has no synced materials.
 */
export const requirementsFromMaterials = (materials) => {
  return Object.keys(materials || {}).map((itemNumber) => {
    const material = materials[itemNumber];
    return {
      itemNumber,
      ...material,
      lps: material.lps || [],
    };
  });
};

/**
 * Resolve a scanned LP against requirement rows (as produced by
 * requirementsFromMaterials). Returns { itemNumber, lpInfo } if found, else null.
 */
export const findLpInRequirements = (requirements, lp) => {
  const normalized = String(lp || '')
    .trim()
    .toUpperCase();
  if (!normalized) {
    return null;
  }
  const match = (requirements || []).reduce((found, req) => {
    if (found) {
      return found;
    }
    const lpInfo = (req.lps || []).find((e) => {
      return String(e.lp).toUpperCase() === normalized;
    });
    return lpInfo ? { itemNumber: req.itemNumber, lpInfo } : null;
  }, null);
  return match;
};

/**
 * Material quantity for display: locale-grouped, with up to `decimals` decimals.
 * The default of 4 matches the precision picks are recorded with (clampPickQty).
 */
export const formatMaterialQty = (qty, decimals = 4) => {
  return (Number(qty) || 0).toLocaleString(undefined, {
    maximumFractionDigits: decimals,
  });
};

/**
 * qtyPerUnit as a material : parts ratio. 2 → "2:1" (2 of material per part),
 * 0.25 → "1:4" (1 of material per 4 parts), 1.5 → "1.5:1". Null when unset.
 */
export const formatMaterialRatio = (qtyPerUnit) => {
  if (!qtyPerUnit) return null;
  if (qtyPerUnit >= 1) return `${formatMaterialQty(qtyPerUnit, 2)}:1`;
  return `1:${formatMaterialQty(1 / qtyPerUnit, 2)}`;
};

// Picked quantities keep up to 4 decimals: materials are weighed or measured
// (lb, gal), and the rounding drops float noise from the ±1 steppers.
const QTY_PRECISION = 10000;

// A typed quantity: digits with an optional decimal part ("12", "0.5", ".5", "3.").
const TYPED_QTY = /^(?:\d+(?:\.\d*)?|\.\d+)$/;

const parsePickQty = (raw) => {
  if (typeof raw === 'number') return raw;
  const typed = String(raw ?? '')
    .trim()
    .replace(',', '.');
  return TYPED_QTY.test(typed) ? Number(typed) : Number.NaN;
};

/** A quantity rounded to the precision picks are recorded with. */
export const roundPickQty = (qty) => {
  return Math.round(qty * QTY_PRECISION) / QTY_PRECISION;
};

/**
 * A quantity rounded down to the precision picks are recorded with, so a plate
 * of 1.23456 gives 1.2345 and never more than the plate holds. The small offset
 * keeps float noise (1.3 stored as 1.29999...) from dropping a step.
 */
export const floorPickQty = (qty) => {
  return Math.floor(qty * QTY_PRECISION + 1e-6) / QTY_PRECISION;
};

/**
 * A picked quantity clamped to 0..maxQty. `raw` may be typed text ("0.5" or
 * "0,5"); anything that is not a whole number or decimal, such as "12abc", is 0.
 * The cap is maxQty rounded down, so typing the plate's own quantity never
 * rounds past it.
 */
export const clampPickQty = (raw, maxQty) => {
  const n = parsePickQty(raw);
  if (!Number.isFinite(n)) return 0;
  const cap = floorPickQty(Math.max(0, Number(maxQty) || 0));
  return Math.min(roundPickQty(Math.max(0, n)), cap);
};

/**
 * Parts on the WOO's triage ledger that consume material: every entry that is
 * not voided (recorded or submitted), except one whose reason group failed for
 * good (status `failed`, `retryable: false`), since D365 never got that RAF.
 * Triage parts come off the good count by part correction, so no labor ticket
 * carries them, but each becomes a Triage RAF that consumes material in D365.
 * Groups are matched by submissionId and reasonId (triage CONNECTOR_CONTRACT).
 */
export const triagedParts = (triage) => {
  const entries = Array.isArray(triage?.entries) ? triage.entries : [];
  const submissions = Array.isArray(triage?.submissions)
    ? triage.submissions
    : [];
  const failedForGood = new Set();
  submissions.forEach((submission) => {
    const groups = Array.isArray(submission?.byReason)
      ? submission.byReason
      : [];
    groups.forEach((group) => {
      if (group?.status === 'failed' && group.retryable === false) {
        failedForGood.add(`${submission.submissionId}:${group.reasonId}`);
      }
    });
  });
  return entries.reduce((sum, entry) => {
    if (entry?.status === 'voided') return sum;
    if (
      entry?.submissionId &&
      failedForGood.has(`${entry.submissionId}:${entry.reasonId}`)
    ) {
      return sum;
    }
    return sum + (Number(entry?.qty) || 0);
  }, 0);
};

/**
 * Pick shortfalls the connector is holding labor tickets for, one per item (the
 * latest), from the WOO's rafResults. An entry that has since posted (it has a
 * journalId) is no longer held.
 */
export const pickShortfallsFrom = (rafResults) => {
  const byItem = Object.values(rafResults || {}).reduce((memo, entry) => {
    const shortfall = entry?.pickShortfall;
    if (!shortfall?.itemNumber || entry.journalId) return memo;
    if (!(Number(shortfall.shortQty) > 0)) return memo;
    const prev = memo[shortfall.itemNumber];
    if (prev && String(prev.at || '') >= String(shortfall.at || '')) {
      return memo;
    }
    return { ...memo, [shortfall.itemNumber]: shortfall };
  }, {});
  return Object.values(byItem).map((shortfall) => {
    return {
      itemNumber: shortfall.itemNumber,
      shortQty: Number(shortfall.shortQty),
      unit: shortfall.unit || null,
      at: shortfall.at || null,
    };
  });
};
